import React, { useEffect, useMemo, useState } from "react";
import api, { formatRupiah, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Flame, Snowflake, Zap, Plus, Phone, Calendar, Trash2, Target } from "lucide-react";
import { toast } from "sonner";

const TAG_META = {
  hot: { icon: Flame, label: "HOT", color: "bg-red-500/15 text-red-300 border-red-500/30" },
  warm: { icon: Zap, label: "WARM", color: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  cold: { icon: Snowflake, label: "COLD", color: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
};

const EMPTY = {
  name: "", phone: "", email: "", needs: "",
  budget_min: "", budget_max: "", preferred_location: "",
  tag: "warm", notes: "", last_viewing_at: "", source: "",
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [matches, setMatches] = useState(null);
  const [showMatches, setShowMatches] = useState(false);

  const load = async () => {
    const { data } = await api.get("/leads");
    setLeads(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => tab === "all" ? leads : leads.filter((l) => l.tag === tab), [leads, tab]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        budget_min: form.budget_min ? Number(form.budget_min) : null,
        budget_max: form.budget_max ? Number(form.budget_max) : null,
        last_viewing_at: form.last_viewing_at ? new Date(form.last_viewing_at).toISOString() : null,
      };
      await api.post("/leads", payload);
      toast.success("Lead baru ditambahkan");
      setForm(EMPTY);
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const changeTag = async (id, tag) => {
    setLeads((p) => p.map((l) => (l.id === id ? { ...l, tag } : l)));
    try { await api.patch(`/leads/${id}/tag`, { tag }); }
    catch (e) { toast.error(formatApiError(e)); load(); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus lead?")) return;
    try { await api.delete(`/leads/${id}`); load(); toast.success("Lead dihapus"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const findMatch = async (id) => {
    try {
      const { data } = await api.get(`/leads/${id}/matches`);
      setMatches({ id, items: data });
      setShowMatches(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <span className="text-xs uppercase tracking-widest text-orange-400 font-semibold">CRM</span>
          <h1 className="text-3xl font-extrabold tracking-tight mt-1">Lead Inbox</h1>
          <p className="text-slate-400 text-sm mt-1">Semua kontak buyer tersentralisasi dengan auto-tag.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-lead-btn" className="bg-gradient-to-r from-[#FF5722] to-[#F97316] hover:from-[#F97316] hover:to-[#EA580C] font-semibold">
              <Plus className="w-4 h-4 mr-1" /> Tambah Lead
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#141C2E] border-white/10 text-slate-100 max-w-xl">
            <DialogHeader><DialogTitle>Lead Baru</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nama *</Label><Input required value={form.name} data-testid="lead-name" onChange={(e) => set("name", e.target.value)} className="mt-1 bg-[#0D1322] border-white/10" placeholder="Pak Hendra" /></div>
                <div><Label>No. WhatsApp</Label><Input value={form.phone} data-testid="lead-phone" onChange={(e) => set("phone", e.target.value)} className="mt-1 bg-[#0D1322] border-white/10" placeholder="+62812..." /></div>
              </div>
              <div><Label>Email</Label><Input type="email" value={form.email} data-testid="lead-email" onChange={(e) => set("email", e.target.value)} className="mt-1 bg-[#0D1322] border-white/10" /></div>
              <div><Label>Kebutuhan</Label><Input value={form.needs} data-testid="lead-needs" onChange={(e) => set("needs", e.target.value)} className="mt-1 bg-[#0D1322] border-white/10" placeholder="Rumah 3KT di BSD" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Budget Min (Rp)</Label><Input type="number" min="0" value={form.budget_min} data-testid="lead-budget-min" onChange={(e) => set("budget_min", e.target.value)} className="mt-1 bg-[#0D1322] border-white/10" /></div>
                <div><Label>Budget Max (Rp)</Label><Input type="number" min="0" value={form.budget_max} data-testid="lead-budget-max" onChange={(e) => set("budget_max", e.target.value)} className="mt-1 bg-[#0D1322] border-white/10" /></div>
              </div>
              <div><Label>Lokasi Preferensi</Label><Input value={form.preferred_location} data-testid="lead-location" onChange={(e) => set("preferred_location", e.target.value)} className="mt-1 bg-[#0D1322] border-white/10" placeholder="BSD, Serpong" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tag</Label>
                  <Select value={form.tag} onValueChange={(v) => set("tag", v)}>
                    <SelectTrigger data-testid="lead-tag" className="mt-1 bg-[#0D1322] border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141C2E] border-white/10 text-slate-100">
                      <SelectItem value="hot">HOT</SelectItem>
                      <SelectItem value="warm">WARM</SelectItem>
                      <SelectItem value="cold">COLD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Tgl. Viewing Terakhir</Label><Input type="date" value={form.last_viewing_at} data-testid="lead-viewing" onChange={(e) => set("last_viewing_at", e.target.value)} className="mt-1 bg-[#0D1322] border-white/10" /></div>
              </div>
              <div><Label>Catatan</Label><Textarea rows={3} value={form.notes} data-testid="lead-notes" onChange={(e) => set("notes", e.target.value)} className="bg-[#0D1322] border-white/10 resize-none" /></div>
              <Button type="submit" data-testid="lead-submit" className="w-full bg-orange-500 hover:bg-orange-600 font-semibold">Simpan Lead</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#141C2E] border border-white/5">
          <TabsTrigger value="all" data-testid="tab-all">Semua ({leads.length})</TabsTrigger>
          <TabsTrigger value="hot" data-testid="tab-hot">🔥 Hot ({leads.filter((x) => x.tag === "hot").length})</TabsTrigger>
          <TabsTrigger value="warm" data-testid="tab-warm">⚡ Warm ({leads.filter((x) => x.tag === "warm").length})</TabsTrigger>
          <TabsTrigger value="cold" data-testid="tab-cold">❄ Cold ({leads.filter((x) => x.tag === "cold").length})</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.length === 0 ? (
          <div className="col-span-full rounded-xl bg-[#141C2E] border border-white/5 p-12 text-center text-slate-400">
            Belum ada lead di kategori ini.
          </div>
        ) : filtered.map((lead) => {
          const Meta = TAG_META[lead.tag];
          return (
            <div key={lead.id} data-testid={`lead-card-${lead.id}`} className="rounded-xl bg-[#141C2E] border border-white/5 p-5 hover:border-orange-500/30 transition">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold text-slate-100">{lead.name}</div>
                  {lead.phone && <a href={`https://wa.me/${lead.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="text-xs text-emerald-300 flex items-center gap-1 mt-1" data-testid={`lead-wa-${lead.id}`}><Phone className="w-3 h-3" /> {lead.phone}</a>}
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${Meta.color} flex items-center gap-1`}>
                  <Meta.icon className="w-3 h-3" /> {Meta.label}
                </span>
              </div>
              {lead.needs && <div className="text-sm text-slate-300 mt-3">{lead.needs}</div>}
              <div className="mt-2 text-xs text-slate-500 space-y-0.5">
                {lead.preferred_location && <div>📍 {lead.preferred_location}</div>}
                {(lead.budget_min || lead.budget_max) && (
                  <div>💰 {lead.budget_min ? formatRupiah(lead.budget_min) : "0"} — {lead.budget_max ? formatRupiah(lead.budget_max) : "∞"}</div>
                )}
              </div>

              {lead.follow_ups?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-white/5 space-y-1.5">
                  {lead.follow_ups.map((f, i) => (
                    <div key={i} className={`text-xs flex items-center gap-1.5 ${f.is_overdue ? "text-red-300" : "text-slate-400"}`}>
                      <Calendar className="w-3 h-3" /> {f.label}: {new Date(f.due_at).toLocaleDateString("id-ID", { day: "2-digit", month: "short" })}
                      {f.is_overdue && <span className="text-[10px] uppercase bg-red-500/20 px-1.5 rounded">overdue</span>}
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <Select value={lead.tag} onValueChange={(v) => changeTag(lead.id, v)}>
                  <SelectTrigger data-testid={`lead-tag-select-${lead.id}`} className="h-8 bg-[#0D1322] border-white/10 text-xs w-[110px]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141C2E] border-white/10 text-slate-100">
                    <SelectItem value="hot">🔥 HOT</SelectItem>
                    <SelectItem value="warm">⚡ WARM</SelectItem>
                    <SelectItem value="cold">❄ COLD</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" className="h-8 border-white/10 bg-[#0D1322] text-xs" onClick={() => findMatch(lead.id)} data-testid={`lead-match-${lead.id}`}>
                  <Target className="w-3 h-3 mr-1" /> Match Listing
                </Button>
                <Button variant="outline" className="h-8 border-red-500/30 text-red-300 bg-red-500/5 hover:bg-red-500/10 text-xs" onClick={() => remove(lead.id)} data-testid={`lead-delete-${lead.id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={showMatches} onOpenChange={setShowMatches}>
        <DialogContent className="bg-[#141C2E] border-white/10 text-slate-100 max-w-2xl">
          <DialogHeader><DialogTitle>Listing yang Cocok</DialogTitle></DialogHeader>
          {matches?.items?.length ? (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {matches.items.map((l) => (
                <div key={l.id} className="p-3 rounded-lg bg-[#0D1322] border border-white/5 flex items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold text-slate-100">{l.title}</div>
                    <div className="text-xs text-slate-400">{l.location} · {formatRupiah(l.price)}</div>
                  </div>
                  <a href={`/app/listings/${l.id}`} className="text-xs text-orange-400 hover:text-orange-300">Lihat →</a>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-slate-400 py-6 text-center">Belum ada listing yang cocok dengan budget &amp; lokasi.</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
